import {
  BookOpenText,
  Bot,
  CalendarSync,
  CodeXml,
  Download,
  Map as MapIcon,
  SquareScissors,
} from "lucide-react";
import type { ReactNode } from "react";
import type { Layout, Layouts } from "react-grid-layout";
import * as Routes from "routes";
import { QuickActionCard, type QuickAction } from "./cards/quick-action-card";
import { WhatsNewCard } from "./cards/whats-new-card";

/**
 * Grid breakpoints and column counts for react-grid-layout, the single source of
 * the dashboard's responsiveness.
 *
 * These are **container** widths: RGL's WidthProvider measures the grid element,
 * so they track the space the grid actually has, which shrinks when the sidebar
 * is open. They are not Tailwind's viewport breakpoints. Tune them here.
 */
export const GRID_BREAKPOINTS = { lg: 1024, md: 768, xs: 0 };
export const GRID_COLS = { lg: 12, md: 6, xs: 1 };
export const GRID_ROW_HEIGHT = 150;
export const GRID_MARGIN: [number, number] = [16, 16];

type Breakpoint = keyof typeof GRID_COLS;
const BREAKPOINTS: Breakpoint[] = ["lg", "md", "xs"];

/** Per-breakpoint size + resize bounds (in grid units) for a block. */
type BlockSizes = Record<
  Breakpoint,
  { w: number; h: number; minW: number; minH: number }
>;

export interface HomeBlockDef {
  id: string;
  title: string;
  description: string;
  sizes: BlockSizes;
  render: (editing: boolean) => ReactNode;
}

/** Uniform quick-action cards: quarter-width on desktop, two-up on tablet, stacked on mobile. */
const QUICK_ACTION_SIZES: BlockSizes = {
  lg: { w: 3, h: 1, minW: 3, minH: 1 },
  md: { w: 3, h: 1, minW: 3, minH: 1 },
  xs: { w: 1, h: 1, minW: 1, minH: 1 },
};

/** The What's New feed: full-width, its four article rows dividing the cell's
 *  height. The rows adapt to any cell the user drags out without scrolling, so
 *  minH 3 is only there to keep them tall enough to read. */
const WHATS_NEW_SIZES: BlockSizes = {
  lg: { w: 12, h: 3, minW: 6, minH: 3 },
  md: { w: 6, h: 3, minW: 3, minH: 3 },
  xs: { w: 1, h: 3, minW: 1, minH: 3 },
};

/**
 * The quick-action cards, in their original order. Each becomes a home block
 * whose `render` delegates to the shared presentational component.
 */
const quickActions: QuickAction[] = [
  {
    id: "explore",
    title: "Explore",
    description:
      "Browse and visualise Geoscape datasets in our interactive data explorer",
    href: Routes.dataExplorer,
    icon: MapIcon,
    color: "bg-chart-4",
  },
  {
    id: "data-catalogue",
    title: "Data Catalogue",
    description:
      "Explore available datasets, their uses and applications in our data catalogue",
    href: Routes.dataCatalogue,
    icon: MapIcon,
    color: "bg-chart-5",
  },
  {
    id: "clip",
    title: "Clip",
    description: "Browse and clip Geoscape data to your areas of interest",
    href: Routes.clipUi,
    icon: SquareScissors,
    color: "bg-chart-6",
  },
  {
    id: "download",
    title: "Download",
    description: "Download your licensed datasets and data products",
    href: Routes.dataDownload,
    icon: Download,
    color: "bg-chart-7",
  },
  {
    id: "batch",
    title: "Batch",
    description: "Perform batch geocoding and validation",
    href: Routes.batchBatches,
    icon: Bot,
    color: "bg-chart-8",
  },
  {
    id: "apps-and-apis",
    title: "Apps & APIs",
    description:
      "Integrate geocoding, validation, and more into your applications",
    href: Routes.developerApiKeys,
    icon: CodeXml,
    color: "bg-chart-3",
  },
  {
    id: "documentation",
    title: "Documentation",
    description: "Specifications, release notes, guides, and API reference",
    href: Routes.docsPage,
    icon: BookOpenText,
    color: "bg-chart-1",
  },
  {
    id: "subscription",
    title: "Subscription",
    description: "Manage your Geoscape Hub subscription and billing details",
    href: Routes.orgSubscription,
    icon: CalendarSync,
    color: "bg-chart-2",
  },
];

export const HOME_BLOCKS: HomeBlockDef[] = [
  ...quickActions.map<HomeBlockDef>((action) => ({
    id: action.id,
    title: action.title,
    description: action.description,
    sizes: QUICK_ACTION_SIZES,
    render: (editing) => <QuickActionCard action={action} editing={editing} />,
  })),
  {
    id: "whats-new",
    title: "What's New",
    description: "Latest updates, releases, and announcements",
    sizes: WHATS_NEW_SIZES,
    render: () => <WhatsNewCard />,
  },
];

export const HOME_BLOCK_MAP: Record<string, HomeBlockDef> = Object.fromEntries(
  HOME_BLOCKS.map((b) => [b.id, b]),
);

// ---------------------------------------------------------------------------
// Layout model. The persisted shape is a set of react-grid-layout layouts (one
// per breakpoint) plus the ids the user has removed from their dashboard.
// Everything below is pure — no React.
// ---------------------------------------------------------------------------

export interface StoredHomeLayout {
  version: 2;
  layouts: Layouts;
  /** Block ids the user removed; excluded from every breakpoint's layout. */
  hidden: string[];
}

/** A block's layout item at a given breakpoint, in the first column of row `y`. */
function sizedItem(def: HomeBlockDef, bp: Breakpoint, y: number): Layout {
  const s = def.sizes[bp];
  return { i: def.id, x: 0, y, w: s.w, h: s.h, minW: s.minW, minH: s.minH };
}

/** The first free row below every item in a layout. */
function bottomRow(items: Layout[]): number {
  return items.reduce((row, l) => Math.max(row, l.y + l.h), 0);
}

/** Pack every block into reading order for one breakpoint (RGL compacts after). */
function packBreakpoint(bp: Breakpoint): Layout[] {
  const cols = GRID_COLS[bp];
  const items: Layout[] = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  for (const def of HOME_BLOCKS) {
    const s = def.sizes[bp];
    if (x + s.w > cols) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    items.push({ i: def.id, x, y, w: s.w, h: s.h, minW: s.minW, minH: s.minH });
    x += s.w;
    rowHeight = Math.max(rowHeight, s.h);
  }
  return items;
}

const DEFAULT_LAYOUTS: Layouts = {
  lg: packBreakpoint("lg"),
  md: packBreakpoint("md"),
  xs: packBreakpoint("xs"),
};

export const DEFAULT_STORED: StoredHomeLayout = {
  version: 2,
  layouts: DEFAULT_LAYOUTS,
  hidden: [],
};

/** Ids of blocks currently on the dashboard (known + not hidden), in registry order. */
export function visibleIds(stored: StoredHomeLayout): string[] {
  const hidden = new Set(stored.hidden);
  return HOME_BLOCKS.map((b) => b.id).filter((id) => !hidden.has(id));
}

/** Each breakpoint's layout limited to the currently-visible blocks. */
export function visibleLayouts(stored: StoredHomeLayout): Layouts {
  const visible = new Set(visibleIds(stored));
  const out: Layouts = {};
  for (const bp of BREAKPOINTS) {
    out[bp] = (stored.layouts[bp] ?? []).filter((l) => visible.has(l.i));
  }
  return out;
}

/** Remove a block from the dashboard — it moves to the Add block palette. */
export function removeBlock(
  stored: StoredHomeLayout,
  id: string,
): StoredHomeLayout {
  if (stored.hidden.includes(id)) return stored;
  return { ...stored, hidden: [...stored.hidden, id] };
}

/**
 * Re-add a hidden block, dropped at the bottom of every breakpoint's layout.
 *
 * RGL's own idiom for "put it last" is `y: Infinity`, but it only normalises the
 * breakpoint currently on screen, so the value survives in the other two and
 * `JSON.stringify` persists it as `null`. The bottom row is computed here instead.
 * Any gap left by hidden blocks still in the layout closes when RGL compacts.
 */
export function addBlock(
  stored: StoredHomeLayout,
  id: string,
): StoredHomeLayout {
  const def = HOME_BLOCK_MAP[id];
  if (!def) return stored;
  const layouts: Layouts = {};
  for (const bp of BREAKPOINTS) {
    const others = (stored.layouts[bp] ?? []).filter((l) => l.i !== id);
    layouts[bp] = [...others, sizedItem(def, bp, bottomRow(others))];
  }
  return {
    version: 2,
    layouts,
    hidden: stored.hidden.filter((h) => h !== id),
  };
}

/** A layout item RGL can position: every coordinate a finite number. */
function isSound(l: Layout): boolean {
  return [l.x, l.y, l.w, l.h].every(
    (n) => typeof n === "number" && Number.isFinite(n),
  );
}

/**
 * Repair a layout read back from storage. RGL rejects a non-finite coordinate
 * outright in development and silently mis-sorts the card in production, so an
 * unsound item is re-created at its registry size and stacked below the rest
 * rather than trusted or dropped. Items for ids no longer in the registry go.
 */
function sanitizeLayouts(layouts: Layouts): Layouts {
  const out: Layouts = {};
  for (const bp of BREAKPOINTS) {
    const persisted = layouts[bp];
    const items = (Array.isArray(persisted) ? persisted : []).filter(
      (l: Layout | null) => !!l && HOME_BLOCK_MAP[l.i] !== undefined,
    );
    const repaired = items.filter(isSound);
    let y = bottomRow(repaired);
    for (const broken of items.filter((l) => !isSound(l))) {
      const def = HOME_BLOCK_MAP[broken.i];
      repaired.push(sizedItem(def, bp, y));
      y += def.sizes[bp].h;
    }
    out[bp] = repaired;
  }
  return out;
}

/** Load a persisted layout, falling back to defaults for anything not v2. */
export function migrate(raw: unknown): StoredHomeLayout {
  if (
    raw &&
    typeof raw === "object" &&
    (raw as StoredHomeLayout).version === 2 &&
    typeof (raw as StoredHomeLayout).layouts === "object"
  ) {
    const stored = raw as StoredHomeLayout;
    return {
      version: 2,
      layouts: sanitizeLayouts(stored.layouts),
      hidden: Array.isArray(stored.hidden) ? stored.hidden : [],
    };
  }
  return DEFAULT_STORED;
}
